import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Mail, Phone, MapPin, Clock, MessageCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const ContactUs = () => {
  return (
    <div className="min-h-screen" data-testid="contact-us-page">
      <Header />
      
      <section className="pt-24 pb-16 bg-gradient-to-b from-primary/10 to-background">
        <div className="container text-center">
          <h1 className="text-4xl md:text-5xl font-serif text-primary mb-4 font-bold">Contact Us</h1>
          <p className="text-lg text-foreground/70 max-w-2xl mx-auto font-medium">
            We're here to help. Reach out to us for any queries or support.
          </p>
        </div>
      </section>

      <section className="py-12 bg-background">
        <div className="container max-w-5xl">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-serif font-bold text-primary mb-6">Get in Touch</h2>
              
              <div className="space-y-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">Email</h3>
                        <a href="mailto:siwongalaxy108@gmail.com" className="text-primary hover:text-primary/80">
                          siwongalaxy108@gmail.com
                        </a>
                        <p className="text-sm text-muted-foreground mt-1">
                          For general inquiries, support, and partnership requests
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">Phone</h3>
                        <a href="tel:+917042106100" className="text-primary hover:text-primary/80">
                          +91 7042106100
                        </a>
                        <p className="text-sm text-muted-foreground mt-1">
                          Available during business hours
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <MessageCircle className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">WhatsApp</h3>
                        <a 
                          href="https://wa.me/917042106100" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary/80"
                        >
                          Chat with us on WhatsApp
                        </a>
                        <p className="text-sm text-muted-foreground mt-1">
                          Quick responses for urgent queries
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">Business Address</h3>
                        <p className="text-foreground/80">
                          <span className="font-semibold text-foreground">Siwon Health Care Resources Pvt. Ltd.</span><br />
                          H.No. 330-B, Mianwali Colony<br />
                          Gurgaon - 122001<br />
                          Haryana, India<br />
                          <span className="text-sm block mt-2 text-foreground/70">
                            <span className="font-semibold">CIN:</span> U85110HR2003PTC069710<br />
                            <span className="font-semibold">GSTIN:</span> 06AAHCS1570M1Z3
                          </span>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">Working Hours</h3>
                        <p className="text-foreground/80">
                          Monday - Friday<br />
                          10:00 AM - 5:30 PM IST
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Closed on weekends and public holidays
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Support Categories */}
            <div>
              <h2 className="text-2xl font-serif font-bold text-primary mb-6">How Can We Help?</h2>
              
              <div className="space-y-4">
                <div className="p-5 bg-muted/30 rounded-lg border border-border">
                  <h3 className="font-semibold text-foreground mb-2">Service Inquiries</h3>
                  <p className="text-sm text-muted-foreground">
                    Questions about our coaching programs, Siwon Harmony App, memberships, or webinars.
                  </p>
                </div>

                <div className="p-5 bg-muted/30 rounded-lg border border-border">
                  <h3 className="font-semibold text-foreground mb-2">Billing & Payments</h3>
                  <p className="text-sm text-muted-foreground">
                    Payment issues, invoice requests, subscription management, or billing questions.
                  </p>
                </div>

                <div className="p-5 bg-muted/30 rounded-lg border border-border">
                  <h3 className="font-semibold text-foreground mb-2">Refund Requests</h3>
                  <p className="text-sm text-muted-foreground">
                    Please include your transaction ID and reason for the refund request.
                  </p>
                </div>

                <div className="p-5 bg-muted/30 rounded-lg border border-border">
                  <h3 className="font-semibold text-foreground mb-2">Technical Support</h3>
                  <p className="text-sm text-muted-foreground">
                    Issues with app access, login problems, or technical difficulties.
                  </p>
                </div>

                <div className="p-5 bg-muted/30 rounded-lg border border-border">
                  <h3 className="font-semibold text-foreground mb-2">Partnership & Collaboration</h3>
                  <p className="text-sm text-muted-foreground">
                    Business partnerships, corporate programs, or collaboration opportunities.
                  </p>
                </div>

                <div className="p-5 bg-muted/30 rounded-lg border border-border">
                  <h3 className="font-semibold text-foreground mb-2">Feedback & Suggestions</h3>
                  <p className="text-sm text-muted-foreground">
                    We value your feedback to improve our services and offerings.
                  </p>
                </div>
              </div>

              <div className="mt-8 p-6 bg-primary/5 rounded-lg">
                <h3 className="font-semibold text-primary mb-3">Response Time</h3>
                <p className="text-sm text-foreground/80">
                  We aim to respond to all inquiries within <strong>24-48 business hours</strong>. 
                  For urgent matters, please reach out via WhatsApp or phone during business hours.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactUs;
